import { Injectable } from "@angular/core";
import { FormGroup } from "@angular/forms";
import { Router, NavigationStart } from "@angular/router";
import { filter } from "rxjs";

@Injectable({ providedIn: 'root' })
export class FormFilterResolver {

  private currentModule: string | null = null;
  private previousModule: string | null = null;

  private readonly FORM_KEY_PREFIX = 'lainox-form';

  constructor( 
    private router: Router
  ) {
    this.router.events
      .pipe(filter((e): e is NavigationStart => e instanceof NavigationStart))
      .subscribe(event => {
        const modulePath = event.url.split('/')[1] || '';
        this.previousModule = this.currentModule;
        this.currentModule = modulePath;
      });
  }

  /**
   * Restores filters from localStorage when navigating back to the same section (e.g. from detail to list).
   * @param onFiltersPatched Optional: called right after restore to sync dependent fields
   *                        (e.g. populate "Type" options based on "Model"). Needed because restore
   *                        uses emitEvent: false so valueChanges does not overwrite freshly restored
   *                        values (e.g. cookingMode would be reset by the subscription on deviceModel).
   */
  resolveFilters(
    formId: string,
    form: FormGroup,
    afterFiltersResolved: () => void,
    onFiltersPatched?: (form: FormGroup) => void
  ): void {
    const storageKey = `${this.FORM_KEY_PREFIX}-${formId}`;

    if ( this.previousModule && this.currentModule && this.previousModule === this.currentModule) {
      this.patchForm(storageKey, form);
      onFiltersPatched?.(form);
    } else {
      this.clearLocalFilter(storageKey);
    }

    if ( afterFiltersResolved ) {
      afterFiltersResolved();
    }

    form.valueChanges.subscribe(() => {
      this.saveLocalFilter(storageKey, form);
    });

  }

  clearAll() {
    for (let i = localStorage.length - 1; i >= 0; i--) {
      const key = localStorage.key(i);
      if ( key && key.startsWith(this.FORM_KEY_PREFIX) ) {
        localStorage.removeItem(key);
      }
    }
  }

  private saveLocalFilter (key: string, form: FormGroup): void {
    localStorage.setItem(key, JSON.stringify(form.getRawValue()));
  }

  /** 
   * emitEvent: false prevents valueChanges from overwriting dependent 
   * fields we just restored (e.g. cookingMode). 
  */
  private patchForm (key: string, form: FormGroup): void {
    const saved = localStorage.getItem(key);
    if (saved) {
      form.patchValue(JSON.parse(saved), { emitEvent: false });
    }
  }

  private clearLocalFilter (key: string): void {
    localStorage.removeItem(key);
  }

}
